/**
 * cropMedia — crops pixels from the visible media.
 *
 * Images/Videos: direct canvas.drawImage from the element (full resolution).
 * PDFs: uses Screen Capture API to screenshot the visible tab, then crops.
 *
 * @param {HTMLElement} mediaEl      — the <img>, <video>, or <iframe> element
 * @param {HTMLElement} containerEl  — the overlay container div
 * @param {{ x, y, width, height }} rect — crop box in display pixels, relative to container
 * @returns {Promise<string>|string} — cropped image as data:image/png URL
 */
export default async function cropMedia(mediaEl, containerEl, rect) {
  if (!mediaEl || !containerEl) {
    throw new Error("Missing media or container element");
  }

  const tagName = mediaEl.tagName.toLowerCase();

  // ── Images and Videos: direct pixel access ──
  if (tagName === "img" || tagName === "video") {
    const natW = tagName === "img" ? mediaEl.naturalWidth : mediaEl.videoWidth;
    const natH = tagName === "img" ? mediaEl.naturalHeight : mediaEl.videoHeight;

    const mediaRect = mediaEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    const offsetX = mediaRect.left - containerRect.left;
    const offsetY = mediaRect.top - containerRect.top;

    const scaleX = natW / mediaRect.width;
    const scaleY = natH / mediaRect.height;

    const srcX = (rect.x - offsetX) * scaleX;
    const srcY = (rect.y - offsetY) * scaleY;
    const srcW = rect.width * scaleX;
    const srcH = rect.height * scaleY;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(srcW);
    canvas.height = Math.round(srcH);

    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      mediaEl,
      Math.round(srcX), Math.round(srcY), Math.round(srcW), Math.round(srcH),
      0, 0, canvas.width, canvas.height
    );

    return canvas.toDataURL("image/png");
  }

  // ── PDFs (iframe): Screen Capture API ──
  if (tagName === "iframe") {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error("Screen Capture API not available in this browser");
    }

    // Request tab capture — user will see a permission prompt
    const stream = await navigator.mediaDevices.getDisplayMedia({
      preferCurrentTab: true,
      video: { frameRate: 1 },
    });

    const track = stream.getVideoTracks()[0];

    // Wait a frame for the capture to stabilize
    await new Promise((r) => setTimeout(r, 100));

    const imageCapture = new ImageCapture(track);
    const bitmap = await imageCapture.grabFrame();
    track.stop();

    // bitmap is the full tab/screen — map container-relative rect to screen coords
    const containerRect = containerEl.getBoundingClientRect();

    // Scale from CSS pixels to captured bitmap pixels
    const scaleX = bitmap.width / window.innerWidth;
    const scaleY = bitmap.height / window.innerHeight;

    const absX = (containerRect.left + rect.x) * scaleX;
    const absY = (containerRect.top + rect.y) * scaleY;
    const absW = rect.width * scaleX;
    const absH = rect.height * scaleY;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(absW);
    canvas.height = Math.round(absH);

    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      bitmap,
      Math.round(absX), Math.round(absY), Math.round(absW), Math.round(absH),
      0, 0, canvas.width, canvas.height
    );

    bitmap.close();
    return canvas.toDataURL("image/png");
  }

  throw new Error(`Unsupported element: ${tagName}`);
}